import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, RefreshCw } from 'lucide-react';
import { Screen } from '@/app/layout/Screen';
import { Button } from '@/components/ui/Button';
import { LoadFailure, PanelSkeleton, StateView } from '@/components/ui/Feedback';
import {
  Badge,
  CircleNumber,
  Kicker,
  Meta,
  PageTitle,
  ProgressBar,
  Row,
  RowList,
} from '@/components/ui/Surface';
import { useBackendCourseStore } from '@/store/backendCourseStore';
import { cleanLessonTitle } from '@/lib/titles';

/**
 * Экран 09 на реальном бэкенде. Уроки модуля.
 *
 * Прогресс приходит вместе со списком уроков, поэтому считать его на клиенте
 * не нужно — расхождение с сервером было бы заметнее пользы.
 *
 * Состояния: загрузка, ошибка с повтором, пусто, список.
 */
export function BackendLessonsScreen() {
  const { moduleId = '' } = useParams();
  const navigate = useNavigate();

  const lessons = useBackendCourseStore((state) => state.lessonsByCourse[moduleId]);
  const progress = useBackendCourseStore((state) => state.progressByCourse[moduleId]);
  const modules = useBackendCourseStore((state) => state.modules);
  const loading = useBackendCourseStore((state) => state.loadingLessons);
  const error = useBackendCourseStore((state) => state.error);
  const loadLessons = useBackendCourseStore((state) => state.loadLessons);

  useEffect(() => {
    if (moduleId) void loadLessons(moduleId);
  }, [moduleId, loadLessons]);

  const module = modules.find((item) => item.course_id === moduleId);
  const title = module?.title ?? 'Модуль';

  if (loading && !lessons) {
    return (
      <Screen subtitle="Модуль" back backTo="/course" tab="course">
        <PanelSkeleton count={4} />
      </Screen>
    );
  }

  if (error && !lessons) {
    return (
      <Screen subtitle="Модуль" back backTo="/course" tab="course">
        <LoadFailure
          title="Не удалось загрузить уроки"
          description={error}
          onRetry={() => void loadLessons(moduleId)}
          secondary={
            <Button variant="ghost" block onClick={() => navigate('/course')}>
              Вернуться к программе
            </Button>
          }
        />
      </Screen>
    );
  }

  if (!lessons || lessons.length === 0) {
    return (
      <Screen subtitle="Модуль" back backTo="/course" tab="course">
        <StateView
          icon={<Check className="h-6 w-6" aria-hidden />}
          title="Уроков пока нет"
          description="В этом модуле ещё не открыты уроки. Загляните позже."
          primary={
            <Button variant="primary" block onClick={() => navigate('/course')}>
              Вернуться к программе
            </Button>
          }
        />
      </Screen>
    );
  }

  const nextLesson = lessons.find((lesson) => !lesson.completed);

  return (
    <Screen subtitle="Модуль" back backTo="/course" tab="course">
      <Kicker>Модуль курса</Kicker>
      <PageTitle>{title}</PageTitle>

      {progress ? (
        <>
          <ProgressBar value={progress.percent} />
          <div className="flex items-center justify-between gap-2">
            <Meta>
              Пройдено {progress.completed} из {progress.total}
            </Meta>
            {/*
              Отметка о сдаче появляется на сервере не сразу: ассистент
              дорабатывает ответ уже после отправки. Обновление вручную
              избавляет от выхода и повторного входа в модуль ради проверки
            */}
            <button
              type="button"
              disabled={loading}
              onClick={() => void loadLessons(moduleId)}
              className="bd-focus flex items-center gap-1.5 rounded-full border border-line px-2.5 py-1.5 text-[11px] text-muted disabled:opacity-50"
            >
              <RefreshCw className={loading ? 'h-3.5 w-3.5 animate-bd-spin' : 'h-3.5 w-3.5'} aria-hidden />
              Обновить
            </button>
          </div>
        </>
      ) : null}

      <RowList className="mt-3">
        {lessons.map((lesson, index) => (
          <div key={lesson.assignment_id}>
            <Row
              icon={<CircleNumber tone={lesson.completed ? 'done' : 'brand'}>{index + 1}</CircleNumber>}
              title={cleanLessonTitle(lesson.title)}
              trailing={
                lesson.completed ? (
                  <Badge tone="success">Сдано</Badge>
                ) : lesson.assignment_id === nextLesson?.assignment_id ? (
                  // В списке из восьми уроков глаз ищет, где остановился:
                  // отметка снимает этот поиск
                  <Badge tone="muted">Следующий</Badge>
                ) : undefined
              }
              chevron={!lesson.completed}
              onClick={() => navigate(`/course/lessons/${lesson.assignment_id}`)}
            />
          </div>
        ))}
      </RowList>
    </Screen>
  );
}
